/**
 * Locale-aware formatting with Intl. Arabic uses Latin digits by default (`ar-u-nu-latn`);
 * a tenant may switch to Arabic-Indic digits (`arab`) — see `setNumberingSystem`.
 */
import type { Language } from "./setup";

export type NumberingSystem = "latn" | "arab";

let numbering: NumberingSystem = "latn";

/** Tenant-level preference (from tenant settings). Applies to Arabic only. */
export function setNumberingSystem(ns: NumberingSystem): void {
  numbering = ns;
}

export function getNumberingSystem(): NumberingSystem {
  return numbering;
}

/** BCP-47 tag used for every Intl formatter. */
export function localeTag(lang: Language, ns: NumberingSystem = numbering): string {
  return lang === "ar" ? `ar-u-nu-${ns}` : "en-GB";
}

export function formatNumber(value: number, lang: Language, opts: Intl.NumberFormatOptions = {}): string {
  return new Intl.NumberFormat(localeTag(lang), opts).format(value);
}

export function formatCompact(value: number, lang: Language): string {
  return formatNumber(value, lang, { notation: "compact", maximumFractionDigits: 1 });
}

/** `ratio` is 0..1. */
export function formatPercent(ratio: number, lang: Language, fractionDigits = 1): string {
  return formatNumber(ratio, lang, {
    style: "percent",
    minimumFractionDigits: 0,
    maximumFractionDigits: fractionDigits,
  });
}

export function formatCurrency(value: number, lang: Language, currency = "USD"): string {
  return formatNumber(value, lang, { style: "currency", currency, maximumFractionDigits: 2 });
}

function toDate(value: Date | string | number): Date {
  return value instanceof Date ? value : new Date(value);
}

export function formatDate(value: Date | string | number, lang: Language, timeZone = "Asia/Riyadh"): string {
  return new Intl.DateTimeFormat(localeTag(lang), { day: "numeric", month: "short", year: "numeric", timeZone }).format(toDate(value));
}

export function formatDateTime(value: Date | string | number, lang: Language, timeZone = "Asia/Riyadh"): string {
  return new Intl.DateTimeFormat(localeTag(lang), {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone,
  }).format(toDate(value));
}

/** Compact h:mm:ss style duration, e.g. "1:05:09" (or "-0:04:00" when overdue). */
export function formatDuration(ms: number, lang: Language): string {
  const sign = ms < 0 ? "-" : "";
  const total = Math.floor(Math.abs(ms) / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const two = (n: number) => formatNumber(n, lang, { minimumIntegerDigits: 2, useGrouping: false });
  return `${sign}${formatNumber(h, lang, { useGrouping: false })}:${two(m)}:${two(s)}`;
}
